import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { trackStartFreeConversion, logEvent } from "@/lib/analytics";
import {
  Dumbbell,
  ArrowRight,
  Brain,
  Lock,
  HeartPulse,
  BookOpen,
  Wind,
  TrendingUp,
  MessageCircle,
  ShieldCheck,
  Flame,
  Users,
  Plus,
  Minus,
  Apple,
  Play,
  X,
  Hand,
  Smile,
  Boxes,
  Activity,
  Heart,
  Baby,
  Compass,
  Wallet,
  Cloud,
} from "lucide-react";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" } },
};

const HERO_VIDEO = `${process.env.PUBLIC_URL || ""}/brand/hero.mp4`;
const HERO_POSTER = `${process.env.PUBLIC_URL || ""}/brand/hero-poster.jpg`;

const MOCKS = [
  { label: "Dashboard", url: "https://static.prod-images.emergentagent.com/jobs/9bec0f6c-56da-45ea-8443-ec68d4962a6d/images/3872d5ef9ea1a885d440960cbfd3711fdab96d8900630ca68a20957fbaf73c65.png" },
  { label: "Daily Check-In", url: "https://static.prod-images.emergentagent.com/jobs/9bec0f6c-56da-45ea-8443-ec68d4962a6d/images/0540b55827b4bf9b053d5bc00fbae0bc996f997cb90d600734b12b8c89ca6e0a.png" },
  { label: "Unload Room", url: "https://static.prod-images.emergentagent.com/jobs/9bec0f6c-56da-45ea-8443-ec68d4962a6d/images/1d7c558f39445e6688e3c992c73275f3a83d97d8a9d33c6b9cfb86e07d3319f3.png" },
  { label: "AI Coach", url: "https://static.prod-images.emergentagent.com/jobs/9bec0f6c-56da-45ea-8443-ec68d4962a6d/images/ce39cb92f66a2c4192626843dbfd70d44c58bdd614b74a2ca5a8f3de668c08d0.png" },
  { label: "Before You React", url: "https://static.prod-images.emergentagent.com/jobs/9bec0f6c-56da-45ea-8443-ec68d4962a6d/images/f12687d9e1a99659901c36af396f02e2a151ff9bbed6322c0d1480b1449fb2a3.png" },
  { label: "Scripture", url: "https://static.prod-images.emergentagent.com/jobs/9bec0f6c-56da-45ea-8443-ec68d4962a6d/images/18d55cad7c4bf41291612f8a7a6d3f20901743d477b8d80593a1cf590bf7242f.png" },
  { label: "Progress", url: "https://static.prod-images.emergentagent.com/jobs/9bec0f6c-56da-45ea-8443-ec68d4962a6d/images/fac8a312e0dbf897f01cd966e678324fb7acf526c067a1af6d1086152e468f12.png" },
];

const WHY = [
  { icon: Brain, title: "Built for how men actually process", body: "No fluff, no clinical jargon. Practical tools that fit between work, family, and everything you carry." },
  { icon: Lock, title: "Completely private", body: "Your journal, your thoughts, your struggles — visible only to you. A space with no judgment." },
  { icon: HeartPulse, title: "Train it like the gym", body: "Mental fitness is a practice. Daily reps build emotional strength, discipline, and resilience over time." },
];

const FEATURES = [
  { icon: Smile, title: "Daily Check-In", body: "A 60-second gut-check on your mood and pressure — the rep that starts every day." },
  { icon: Wind, title: "Before You React", body: "Breathe, see the consequences, and choose the stronger move before you blow it up." },
  { icon: MessageCircle, title: "AI Coach", body: "A trusted older-brother voice that listens, asks the right questions, and gives you a plan." },
  { icon: Lock, title: "Journal", body: "Private text & voice journaling with AI reflections that help you actually process." },
  { icon: Hand, title: "Prayer", body: "Guided, optional prayers for the weight you're carrying — save and revisit them." },
  { icon: BookOpen, title: "Scripture", body: "A shared daily Scripture, broken down in plain language for real life." },
  { icon: Users, title: "Brotherhood", body: "Anonymous community and private accountability groups. You're not alone." },
  { icon: TrendingUp, title: "Progress Tracking", body: "Mood trends, streaks, XP and badges that show the work is paying off." },
];

const PROBLEMS = [
  { icon: Boxes, title: "Carrying stress alone" },
  { icon: Flame, title: "Anger" },
  { icon: Activity, title: "Anxiety" },
  { icon: Heart, title: "Marriage" },
  { icon: Baby, title: "Fatherhood" },
  { icon: Compass, title: "Purpose" },
  { icon: Wallet, title: "Finances" },
  { icon: Cloud, title: "Loneliness" },
];

const FAQS = [
  { q: "Is UnloadDaily a therapy app?", a: "No. UnloadDaily supports mental fitness, reflection, leadership growth, and optional faith-based encouragement. It is not a replacement for professional medical care, therapy, or emergency support." },
  { q: "Is my journal really private?", a: "Yes. Your journal entries and reflections are visible only to you. Community posts are anonymous." },
  { q: "Do I have to use the faith content?", a: "Not at all. Faith-based content is fully optional — toggle it on or off anytime in settings." },
  { q: "How much does it cost?", a: "You can start free. UnloadDaily Plus unlocks unlimited AI coaching, the full Scripture library, and premium tools for $7.99/month or $59.99/year, with a 7-day free trial." },
  { q: "Can I cancel anytime?", a: "Yes. Cancel anytime and keep access until the end of your billing period. No games." },
];

function Nav() {
  return (
    <nav className="fixed top-0 inset-x-0 z-50 backdrop-blur-xl bg-black/70 border-b border-white/10">
      <div className="max-w-6xl mx-auto px-5 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2" data-testid="landing-logo">
          <div className="h-8 w-8 rounded-lg bg-primary grid place-items-center">
            <Dumbbell size={16} className="text-white" />
          </div>
          <span className="heading text-2xl font-black tracking-tighter">
            UNLOAD<span className="text-primary">DAILY</span>
          </span>
        </Link>
        <div className="hidden md:flex items-center gap-8 text-sm text-muted-foreground">
          <a href="#why" className="hover:text-white transition-colors">Why</a>
          <a href="#preview" className="hover:text-white transition-colors">Preview</a>
          <a href="#features" className="hover:text-white transition-colors">Features</a>
          <a href="#faq" className="hover:text-white transition-colors">FAQ</a>
        </div>
        <Link
          to="/app"
          data-testid="open-app-btn"
          className="bg-white text-black font-bold text-sm rounded-lg px-4 py-2 hover:bg-white/90 transition-colors"
        >
          Open App
        </Link>
      </div>
    </nav>
  );
}

function Section({ children, className = "", id }) {
  return (
    <section id={id} className={`max-w-6xl mx-auto px-5 ${className}`}>
      {children}
    </section>
  );
}

function FaqItem({ q, a, i }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-white/10" data-testid={`faq-${i}`}>
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between py-5 text-left"
      >
        <span className="font-semibold text-lg pr-4">{q}</span>
        {open ? <Minus size={20} className="text-primary shrink-0" /> : <Plus size={20} className="text-primary shrink-0" />}
      </button>
      {open && <p className="text-muted-foreground pb-5 leading-relaxed">{a}</p>}
    </div>
  );
}

function StatStrip() {
  const [stats, setStats] = useState(null);
  useEffect(() => {
    const base = process.env.REACT_APP_BACKEND_URL;
    fetch(`${base}/api/public/stats`).then((r) => r.json()).then(setStats).catch(() => setStats(null));
  }, []);
  const low = !stats || (stats.members || 0) < 25;
  const items = [
    { n: stats?.members, label: "Men training" },
    { n: stats?.checkins, label: "Check-ins logged" },
    { n: stats?.journals, label: "Journals written" },
  ];
  return (
    <Section className="py-8 border-y border-white/5" >
      {low ? (
        <p className="text-center heading text-2xl sm:text-3xl font-black tracking-tight" data-testid="social-proof-fallback">
          Join our <span className="text-primary">growing community.</span>
        </p>
      ) : (
        <div className="grid grid-cols-3 gap-4 max-w-3xl mx-auto" data-testid="social-proof-stats">
          {items.map((it) => (
            <div key={it.label} className="text-center">
              <p className="heading text-3xl sm:text-4xl font-black text-primary">{(it.n || 0).toLocaleString()}</p>
              <p className="text-xs uppercase tracking-widest text-muted-foreground mt-1">{it.label}</p>
            </div>
          ))}
        </div>
      )}
    </Section>
  );
}

function VideoModal({ open, onClose }) {
  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] bg-black/90 backdrop-blur-sm grid place-items-center p-4"
          onClick={onClose}
          data-testid="video-modal"
        >
          <button
            onClick={onClose}
            data-testid="video-modal-close"
            className="absolute top-5 right-5 h-10 w-10 grid place-items-center rounded-full bg-white/10 hover:bg-white/20 text-white z-10 transition-colors"
            aria-label="Close video"
          >
            <X size={20} />
          </button>
          <motion.div
            initial={{ scale: 0.94, y: 16 }}
            animate={{ scale: 1, y: 0 }}
            exit={{ scale: 0.94, y: 16 }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-[400px] aspect-[9/16] rounded-2xl overflow-hidden border border-primary/30 shadow-[0_0_70px_rgba(255,87,34,0.28)]"
          >
            <video
              src={HERO_VIDEO}
              poster={HERO_POSTER}
              autoPlay
              controls
              playsInline
              className="w-full h-full object-cover bg-black"
            />
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function HeroVideo({ onOpen }) {
  const viewedRef = useRef(false);
  const completedRef = useRef(false);
  const onPlaying = () => {
    if (!viewedRef.current) {
      viewedRef.current = true;
      logEvent("hero_video_viewed", {});
    }
  };
  const onTimeUpdate = (e) => {
    const v = e.currentTarget;
    if (!viewedRef.current && v.currentTime > 0) {
      viewedRef.current = true;
      logEvent("hero_video_viewed", {});
    }
    if (!completedRef.current && v.duration && v.currentTime >= v.duration - 0.6) {
      completedRef.current = true;
      logEvent("hero_video_completed", {});
    }
  };
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.96 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.7, delay: 0.15, ease: "easeOut" }}
      className="relative mx-auto w-full max-w-[290px] sm:max-w-[330px]"
      data-testid="hero-video-wrap"
    >
      <div className="absolute -inset-6 rounded-[3rem] bg-primary/20 blur-3xl pointer-events-none" aria-hidden />
      <motion.button
        type="button"
        onClick={onOpen}
        animate={{ y: [0, -10, 0] }}
        transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
        className="group relative block w-full aspect-[9/16] rounded-3xl overflow-hidden border border-white/10 ring-1 ring-primary/20 shadow-[0_30px_80px_-20px_rgba(0,0,0,0.85)]"
        data-testid="hero-video"
        aria-label="Watch the UnloadDaily story with sound"
      >
        <video
          src={HERO_VIDEO}
          poster={HERO_POSTER}
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          onPlaying={onPlaying}
          onTimeUpdate={onTimeUpdate}
          className="w-full h-full object-cover bg-black"
        />
        <span className="absolute inset-0 grid place-items-center bg-black/10 opacity-0 group-hover:opacity-100 transition-opacity">
          <span className="h-16 w-16 rounded-full bg-primary/90 grid place-items-center shadow-lg">
            <Play size={26} className="text-white translate-x-0.5" fill="currentColor" />
          </span>
        </span>
      </motion.button>
    </motion.div>
  );
}

export default function Landing() {
  const [videoOpen, setVideoOpen] = useState(false);
  useEffect(() => {
    logEvent("landing_view", { path: "/" });
  }, []);
  const openVideoFromVideo = () => {
    setVideoOpen(true);
    logEvent("hero_video_clicked", {});
  };
  const openVideoFromButton = () => {
    setVideoOpen(true);
    logEvent("watch_our_story_clicked", {});
  };
  return (
    <div className="min-h-screen bg-background text-foreground grain relative overflow-x-hidden">
      <Nav />
      <VideoModal open={videoOpen} onClose={() => setVideoOpen(false)} />

      {/* Hero */}
      <header className="relative pt-28 pb-16 sm:pt-32 sm:pb-24">
        <div
          className="absolute inset-0 opacity-40 pointer-events-none"
          style={{
            backgroundImage:
              "radial-gradient(circle at 20% 15%, rgba(255,87,34,0.22), transparent 42%), radial-gradient(circle at 88% 78%, rgba(0,229,255,0.09), transparent 45%)",
          }}
        />
        <Section className="relative z-10">
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-14 items-center">
            {/* Left — copy */}
            <motion.div
              initial="hidden"
              animate="show"
              variants={{ hidden: {}, show: { transition: { staggerChildren: 0.11, delayChildren: 0.1 } } }}
              className="order-2 lg:order-1 text-center lg:text-left"
            >
              <motion.p variants={fadeUp} className="text-primary text-sm font-medium italic max-w-md mx-auto lg:mx-0 mb-5">
                "Because even the strongest men need a place to unload."
              </motion.p>
              <motion.h1 variants={fadeUp} className="heading text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter uppercase leading-[0.95]">
                The Mental Gym<br />For Men
              </motion.h1>
              <motion.p variants={fadeUp} className="heading text-xl sm:text-2xl font-bold mt-5 leading-tight">
                Train Your Mind.<br />Strengthen Your Spirit.<br /><span className="text-primary">Lead With Purpose.</span>
              </motion.p>
              <motion.div variants={fadeUp} className="text-muted-foreground text-base mt-6 leading-relaxed max-w-md mx-auto lg:mx-0 space-y-3">
                <p>Life doesn't stop.</p>
                <p className="flex flex-wrap gap-x-3 justify-center lg:justify-start font-semibold text-white/90">
                  <span>Work.</span><span>Family.</span><span>Pressure.</span><span>Responsibilities.</span>
                </p>
                <p>UnloadDaily helps men build healthier daily habits through reflection, prayer, journaling, AI coaching, and intentional growth.</p>
              </motion.div>
              <motion.div variants={fadeUp} className="flex flex-col sm:flex-row items-center lg:items-start justify-center lg:justify-start gap-3 mt-8">
                <Link
                  to="/app/register"
                  data-testid="hero-start-free-btn"
                  onClick={() => trackStartFreeConversion("hero")}
                  className="w-full sm:w-auto bg-primary text-white font-bold tracking-widest uppercase rounded-lg px-8 py-4 hover:shadow-[0_0_28px_rgba(255,87,34,0.5)] transition-all flex items-center justify-center gap-2"
                >
                  Start My Journey <ArrowRight size={18} />
                </Link>
                <button
                  type="button"
                  data-testid="hero-watch-story-btn"
                  onClick={openVideoFromButton}
                  className="w-full sm:w-auto border border-white/20 text-white font-bold tracking-widest uppercase rounded-lg px-8 py-4 hover:border-white/50 transition-all flex items-center justify-center gap-2"
                >
                  <Play size={16} fill="currentColor" /> Watch Our Story
                </button>
              </motion.div>
              <motion.p variants={fadeUp} className="text-xs text-muted-foreground mt-4">
                Free to start · 7-day Plus trial · Cancel anytime
              </motion.p>
            </motion.div>

            {/* Right — cinematic brand video */}
            <div className="order-1 lg:order-2">
              <HeroVideo onOpen={openVideoFromVideo} />
            </div>
          </div>
        </Section>
      </header>

      <StatStrip />

      {/* Problem */}
      <Section id="problem" className="py-20 sm:py-28">
        <div className="max-w-2xl mb-12">
          <p className="text-xs font-semibold tracking-[0.2em] uppercase text-primary mb-2">What you're carrying</p>
          <h2 className="heading text-4xl sm:text-5xl font-black tracking-tighter uppercase leading-none">
            You've been holding it together for everyone else.
          </h2>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="problem-grid">
          {PROBLEMS.map((p, i) => (
            <motion.div
              key={p.title}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.35, delay: i * 0.05 }}
              className="bg-[#111111] border border-white/10 rounded-xl p-5 flex flex-col items-center text-center gap-3"
            >
              <div className="h-11 w-11 rounded-xl bg-primary/10 grid place-items-center">
                <p.icon size={20} className="text-primary" />
              </div>
              <span className="text-sm font-semibold">{p.title}</span>
            </motion.div>
          ))}
        </div>
      </Section>

      {/* Solution — The Mental Gym */}
      <Section className="py-20 sm:py-24">
        <div className="relative rounded-3xl border border-white/10 bg-gradient-to-b from-primary/10 to-transparent p-10 sm:p-16 text-center overflow-hidden">
          <div className="h-16 w-16 rounded-2xl bg-primary grid place-items-center mx-auto mb-6 glow-orange">
            <Dumbbell size={30} className="text-white" />
          </div>
          <p className="text-xs font-semibold tracking-[0.25em] uppercase text-primary mb-3">The Mental Gym for Men</p>
          <h2 className="heading text-4xl sm:text-6xl font-black tracking-tighter uppercase leading-[0.95] max-w-3xl mx-auto">
            Our bodies need a gym.<br /><span className="text-primary">Our minds need one too.</span>
          </h2>
          <p className="text-muted-foreground text-base sm:text-lg max-w-2xl mx-auto mt-6 leading-relaxed">
            Mental strength isn't a one-time fix — it's a daily practice. UnloadDaily gives you the reps: reflect,
            reset, pray, and lead. Five minutes a day, and you get stronger.
          </p>
          <Link
            to="/app/register"
            data-testid="solution-cta-btn"
            onClick={() => trackStartFreeConversion("solution")}
            className="inline-flex items-center gap-2 mt-8 bg-primary text-white font-bold tracking-widest uppercase rounded-lg px-8 py-4 hover:shadow-[0_0_28px_rgba(255,87,34,0.5)] transition-all"
          >
            Start My Journey <ArrowRight size={18} />
          </Link>
        </div>
      </Section>

      {/* Why */}
      <Section id="why" className="py-20 sm:py-28">
        <div className="max-w-2xl mb-14">
          <p className="text-xs font-semibold tracking-[0.2em] uppercase text-primary mb-2">Why UnloadDaily</p>
          <h2 className="heading text-4xl sm:text-5xl font-black tracking-tighter uppercase leading-none">
            Most men were never taught how to carry this.
          </h2>
        </div>
        <div className="grid md:grid-cols-3 gap-5">
          {WHY.map((w, i) => (
            <motion.div
              key={w.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: i * 0.1 }}
              className="bg-[#111111] border border-white/10 rounded-xl p-6"
            >
              <div className="h-12 w-12 rounded-xl bg-primary/15 grid place-items-center mb-4">
                <w.icon className="text-primary" size={24} />
              </div>
              <h3 className="font-bold text-lg mb-2">{w.title}</h3>
              <p className="text-muted-foreground text-sm leading-relaxed">{w.body}</p>
            </motion.div>
          ))}
        </div>
      </Section>

      {/* App preview */}
      <Section id="preview" className="py-20 sm:py-28">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <p className="text-xs font-semibold tracking-[0.2em] uppercase text-accent mb-2">See it in action</p>
          <h2 className="heading text-4xl sm:text-5xl font-black tracking-tighter uppercase leading-none">
            Everything you need, in your pocket.
          </h2>
        </div>
        <div className="flex gap-5 overflow-x-auto pb-6 -mx-5 px-5 snap-x" data-testid="app-preview-scroll">
          {MOCKS.map((m, i) => (
            <motion.div
              key={m.label}
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: (i % 3) * 0.08 }}
              className="snap-center shrink-0 w-[230px]"
            >
              <div className="rounded-2xl overflow-hidden border border-white/10 bg-[#111111]">
                <img src={m.url} alt={`UnloadDaily ${m.label} screen`} loading="lazy" className="w-full h-auto block" />
              </div>
              <p className="text-center text-sm font-semibold mt-3 text-muted-foreground">{m.label}</p>
            </motion.div>
          ))}
        </div>
      </Section>

      {/* Features */}
      <Section id="features" className="py-20 sm:py-28">
        <div className="max-w-2xl mb-14">
          <p className="text-xs font-semibold tracking-[0.2em] uppercase text-primary mb-2">Features</p>
          <h2 className="heading text-4xl sm:text-5xl font-black tracking-tighter uppercase leading-none">
            Practical tools for real pressure.
          </h2>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {FEATURES.map((f, i) => (
            <motion.div
              key={f.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: (i % 3) * 0.08 }}
              className="bg-[#111111] border border-white/10 rounded-xl p-6 hover:border-primary/40 transition-colors"
            >
              <f.icon className="text-accent mb-4" size={26} />
              <h3 className="font-bold text-lg mb-2">{f.title}</h3>
              <p className="text-muted-foreground text-sm leading-relaxed">{f.body}</p>
            </motion.div>
          ))}
        </div>
      </Section>

      {/* Founder story */}
      <Section className="py-20 sm:py-28">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="max-w-3xl mx-auto"
        >
          <div className="text-center mb-10">
            <p className="text-xs font-semibold tracking-[0.2em] uppercase text-primary mb-3">The Founder's Story</p>
            <h2 className="heading text-4xl sm:text-5xl font-black tracking-tighter uppercase leading-none">
              Why I Built <span className="text-primary">UnloadDaily</span>
            </h2>
          </div>

          <div className="space-y-5 text-muted-foreground leading-relaxed text-base sm:text-lg">
            <p>
              There was a season in my life when I realized something that many men quietly experience — we're expected
              to carry everything.
            </p>
            <div className="flex flex-wrap gap-2">
              {["Work responsibilities", "Financial pressure", "Marriage", "Fatherhood", "Leadership"].map((t) => (
                <span key={t} className="text-sm font-semibold text-white border border-white/15 rounded-full px-4 py-1.5">
                  {t}
                </span>
              ))}
            </div>
            <p>
              The expectations never seem to stop, yet very few men are taught how to process what they're carrying in a
              healthy way. Like many others, I've worked demanding jobs where long hours, responsibility, and pressure
              become part of everyday life. I began asking myself a simple question:
            </p>
            <p className="font-read text-2xl sm:text-3xl text-white leading-snug border-l-2 border-primary pl-5">
              Who checks on the man everyone depends on?
            </p>
            <p>
              That question became the foundation of UnloadDaily. I didn't want to build just another mental health app.
              I wanted to create a daily system that helps men become mentally stronger, emotionally healthier, and
              better leaders — a place where a man can privately reflect, journal, track his emotional health, slow down
              before reacting, learn practical leadership principles, and, if he chooses, grow spiritually through
              Scripture and prayer.
            </p>
            <p>
              UnloadDaily isn't about pretending to have all the answers. It's about creating space for honest growth.
            </p>
            <div className="space-y-1 text-white font-semibold">
              <p>I believe healthier men build healthier marriages.</p>
              <p>Healthier fathers raise healthier children.</p>
              <p>Healthier leaders strengthen communities.</p>
            </div>
            <p>
              If UnloadDaily helps even one man become more present for his family, more disciplined in his decisions, or
              more hopeful during a difficult season, then this mission is worth it. This isn't just an app. It's a
              movement to help men become the leaders they were created to be — one day at a time.
            </p>
          </div>

          <div className="mt-12 text-center border-t border-white/10 pt-10">
            <p className="font-read text-3xl sm:text-4xl text-white leading-snug">
              Every man carries something.
            </p>
            <p className="font-read text-3xl sm:text-4xl text-primary leading-snug">
              You don't have to carry it alone.
            </p>
            <p className="text-muted-foreground mt-6 max-w-xl mx-auto">
              Join thousands of men building stronger minds, stronger habits, stronger families, and stronger futures.
            </p>
            <Link
              to="/app/register"
              data-testid="founder-start-btn"
              onClick={() => trackStartFreeConversion("founder")}
              className="inline-flex items-center gap-2 mt-7 bg-primary text-white font-bold tracking-widest uppercase rounded-lg px-8 py-4 hover:shadow-[0_0_28px_rgba(255,87,34,0.5)] transition-all"
            >
              Start Your Journey Today <ArrowRight size={18} />
            </Link>
          </div>
        </motion.div>
      </Section>

      {/* FAQ */}
      <Section id="faq" className="py-20 sm:py-28">
        <div className="max-w-2xl mx-auto">
          <h2 className="heading text-4xl sm:text-5xl font-black tracking-tighter uppercase leading-none mb-10 text-center">
            Questions
          </h2>
          {FAQS.map((f, i) => (
            <FaqItem key={i} q={f.q} a={f.a} i={i} />
          ))}
        </div>
      </Section>

      {/* Download */}
      <Section className="py-20">
        <div className="bg-gradient-to-br from-primary/20 to-transparent border border-primary/30 rounded-3xl p-10 sm:p-14 text-center">
          <h2 className="heading text-4xl sm:text-6xl font-black tracking-tighter uppercase leading-none">
            Start carrying it <span className="text-primary">better.</span>
          </h2>
          <p className="text-muted-foreground mt-4 max-w-xl mx-auto">
            Begin free in your browser today. Mobile apps are on the way.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mt-8">
            <Link
              to="/app/register"
              data-testid="cta-start-free-btn"
              onClick={() => trackStartFreeConversion("footer_cta")}
              className="w-full sm:w-auto bg-primary text-white font-bold tracking-widest uppercase rounded-lg px-8 py-4 hover:shadow-[0_0_28px_rgba(255,87,34,0.5)] transition-all flex items-center justify-center gap-2"
            >
              Start Free <ArrowRight size={18} />
            </Link>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-3 mt-6">
            <div className="flex items-center gap-2 border border-white/15 rounded-lg px-5 py-3 opacity-70" data-testid="download-ios">
              <Apple size={22} />
              <div className="text-left leading-tight">
                <p className="text-[10px] text-muted-foreground uppercase">Coming soon</p>
                <p className="font-semibold text-sm">App Store</p>
              </div>
            </div>
            <div className="flex items-center gap-2 border border-white/15 rounded-lg px-5 py-3 opacity-70" data-testid="download-android">
              <Play size={20} />
              <div className="text-left leading-tight">
                <p className="text-[10px] text-muted-foreground uppercase">Coming soon</p>
                <p className="font-semibold text-sm">Google Play</p>
              </div>
            </div>
          </div>
        </div>
      </Section>

      {/* Safe messaging */}
      <Section className="pb-12">
        <div className="flex items-start gap-3 max-w-3xl mx-auto text-muted-foreground">
          <ShieldCheck size={18} className="text-accent shrink-0 mt-0.5" />
          <p className="text-xs leading-relaxed">
            UnloadDaily supports mental fitness, reflection, leadership growth, and optional faith-based encouragement.
            It is not a replacement for professional medical care, therapy, or emergency support.
          </p>
        </div>
      </Section>

      {/* Footer */}
      <footer className="border-t border-white/10 py-12">
        <Section>
          <div className="flex flex-col sm:flex-row justify-between gap-8">
            <div>
              <span className="heading text-2xl font-black tracking-tighter">
                UNLOAD<span className="text-primary">DAILY</span>
              </span>
              <p className="text-sm text-muted-foreground mt-2 max-w-xs">
                The private mental gym built for men. Train your emotional strength.
              </p>
            </div>
            <div className="flex flex-wrap gap-x-12 gap-y-6 text-sm">
              <div className="flex flex-col gap-2">
                <p className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Product</p>
                <Link to="/app" className="hover:text-primary transition-colors">Open App</Link>
                <Link to="/app/register" className="hover:text-primary transition-colors">Start Free</Link>
                <Link to="/app/subscribe" className="hover:text-primary transition-colors">Pricing</Link>
              </div>
              <div className="flex flex-col gap-2">
                <p className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Legal</p>
                <Link to="/privacy" data-testid="footer-privacy" className="hover:text-primary transition-colors">Privacy Policy</Link>
                <Link to="/terms" data-testid="footer-terms" className="hover:text-primary transition-colors">Terms of Service</Link>
              </div>
              <div className="flex flex-col gap-2">
                <p className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Connect</p>
                <a href="mailto:support@unloaddaily.com" className="hover:text-primary transition-colors">Contact</a>
                <a href="https://instagram.com/unloaddaily" target="_blank" rel="noreferrer" className="hover:text-primary transition-colors">Instagram</a>
                <a href="https://x.com/unloaddaily" target="_blank" rel="noreferrer" className="hover:text-primary transition-colors">X / Twitter</a>
              </div>
            </div>
          </div>
          <p className="text-xs text-muted-foreground mt-10">© 2026 UnloadDaily. All rights reserved.</p>
        </Section>
      </footer>
    </div>
  );
}
